import React, { useEffect, useMemo, useRef, useState } from "react";

/*
  PatAtomPool.jsx
  -----------------------
  Pre-DAG variable confirmation.
  Purpose: allow the user to confirm candidate concepts, nominate exactly one
  focal outcome (Y), assign provisional roles to the remaining concepts, and
  promote the confirmed set into DAG construction.

  Key requirements:
   - Do NOT reset selection / edits on parent re-render
   - Outcome (Y) is explicitly selectable among selected items
   - Support both onConfirm and onConfirmed prop names
*/
const SUSPICIOUS_LABEL_RE =
  /\b(and|or|when|while|because|through|via|if|unless|mediated by|moderated by|associated with|correlated with|related to)\b/i;

const ATOM_POOL_GRID_TEMPLATE = "52px 64px minmax(120px, 140px) minmax(0, 1fr)";
const OUTCOME_ROLE_VALUE = "__Y__";

function normaliseLabelText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isSuspiciousLabel(value) {
  const text = normaliseLabelText(value);
  if (!text) return false;
  return SUSPICIOUS_LABEL_RE.test(text);
}

function suspiciousLabelReason(value) {
  const text = normaliseLabelText(value);
  if (!text) return "";

  if (/\bmediated by\b/i.test(text)) {
    return "Contains mediation scaffolding rather than a clean concept.";
  }
  if (/\bmoderated by\b/i.test(text)) {
    return "Contains moderation scaffolding rather than a clean concept.";
  }
  if (/\b(and|or)\b/i.test(text)) {
    return "May combine multiple concepts or outcomes in one label.";
  }
  if (/\b(when|while|because|if|unless)\b/i.test(text)) {
    return "Looks clause-like rather than concept-like.";
  }
  if (/\b(through|via)\b/i.test(text)) {
    return "Contains pathway scaffolding rather than a clean concept.";
  }
  if (/\b(associated with|correlated with|related to)\b/i.test(text)) {
    return "Contains relational wording rather than a clean concept.";
  }

  return "Review this label before promotion.";
}

function getSuggestedRole(atom, index) {
  const role = atom?.role;
  if (role === "X" || role === "M" || role === "Z") return role;
  return "";
}

export default function PatAtomPool({
  atoms = [],
  onConfirm,
  onConfirmed,
  onAtomsChange,
}) {
  const emit = onConfirm || onConfirmed;

  const [localAtoms, setLocalAtoms] = useState([]);
  const [outcomeId, setOutcomeId] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  const lastEmittedSignatureRef = useRef(null);
  const lastSignatureRef = useRef(null);
  const isHydratingRef = useRef(false);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    if (typeof onAtomsChange !== "function") return;

    const payload = localAtoms.map(({ id, label, role, selected }) => ({
      id,
      label,
      role,
      selected,
    }));

    const signature = JSON.stringify(payload);
    if (signature === lastEmittedSignatureRef.current) return;

    lastEmittedSignatureRef.current = signature;
    onAtomsChange(payload);
  }, [localAtoms, onAtomsChange]);

  const incomingSignature = useMemo(() => {
    if (!Array.isArray(atoms)) return "[]";
    const sig = atoms
      .map((a, i) => ({
        id: a?.id ?? a?.atom_id ?? `A${i + 1}`,
        label: (a?.label ?? a?.text ?? a?.raw ?? `Concept ${i + 1}`) + "",
        role: a?.role ?? "",
      }))
      .sort((x, y) => x.id.localeCompare(y.id));
    return JSON.stringify(sig);
  }, [atoms]);

  useEffect(() => {
    if (incomingSignature === lastSignatureRef.current) return;
    lastSignatureRef.current = incomingSignature;
    isHydratingRef.current = true;

    if (!Array.isArray(atoms)) {
      setLocalAtoms((prev) => (prev.length === 0 ? prev : []));
      setOutcomeId((prev) => (prev === null ? prev : null));
      setAcknowledged(false);
      return;
    }

    const incoming = atoms.map((a, i) => {
      const role = a?.role ?? "";
      const isOutcome = role === "Y";

      return {
        id: a?.id ?? a?.atom_id ?? `A${i + 1}`,
        label:
          (a?.label ??
            a?.text ??
            a?.raw ??
            a?.source_text ??
            `Concept ${i + 1}`) + "",
        role: isOutcome ? "" : getSuggestedRole(a, i),
        selected: Boolean(a?.selected) || isOutcome,
        _isOutcome: isOutcome,
      };
    });

    setLocalAtoms((prev) => {
      const prevById = new Map(prev.map((p) => [p.id, p]));
      const next = incoming.map((inc) => {
        const existing = prevById.get(inc.id);
        return {
          id: inc.id,
          label:
            existing && typeof existing.label === "string"
              ? existing.label
              : inc.label,
          role: existing?.role ?? inc.role,
          selected: existing ? !!existing.selected : !!inc.selected,
        };
      });

      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });

    setOutcomeId((prev) => {
      const explicitY = incoming.find((x) => x._isOutcome);
      if (explicitY) return explicitY.id;

      const stillExists = incoming.some((x) => x.id === prev);
      return stillExists ? prev : null;
    });
    setAcknowledged(false);
  }, [incomingSignature]);

  const updateAtom = (id, patch) => {
    setAcknowledged(false);
    setLocalAtoms((xs) =>
      xs.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );
  };

  const toggleSelected = (id) => {
    setAcknowledged(false);

    setLocalAtoms((xs) =>
      xs.map((a) => (a.id === id ? { ...a, selected: !a.selected } : a)),
    );

    setOutcomeId((current) => (current === id ? null : current));
  };

  const chooseOutcome = (id) => {
    setAcknowledged(false);
    setOutcomeId(id);
    setLocalAtoms((xs) =>
      xs.map((a) => ({
        ...a,
        selected: a.id === id ? true : a.selected,
      })),
    );
  };

  const updateLabel = (id, label) => {
    updateAtom(id, { label });
  };

  const updateRole = (id, role) => {
    setAcknowledged(false);
    updateAtom(id, { role });
  };

  const addAtom = () => {
    const label = newLabel.trim();
    if (!label) return;

    setAcknowledged(false);

    const id = `user-${Date.now()}`;
    setLocalAtoms((xs) => [
      ...xs,
      {
        id,
        label,
        role: "",
        selected: false,
      },
    ]);
    setNewLabel("");
  };

  const selectedAtoms = localAtoms.filter((a) => a.selected);
  const selectedOutcome = selectedAtoms.find((a) => a.id === outcomeId) || null;
  const selectedNonOutcome = selectedAtoms.filter((a) => a.id !== outcomeId);

  const outcomeLooksSuspicious = isSuspiciousLabel(
    selectedOutcome?.label || "",
  );
  const missingRoleCount = selectedNonOutcome.filter((a) => !a.role).length;

  const canPromote =
    !!outcomeId &&
    selectedNonOutcome.length >= 1 &&
    !outcomeLooksSuspicious &&
    missingRoleCount === 0 &&
    acknowledged;

  const promote = () => {
    if (!outcomeId) return;

    const outcome = selectedAtoms.find((a) => a.id === outcomeId);
    if (!outcome) return;

    const nonOutcome = selectedAtoms.filter((a) => a.id !== outcomeId);
    if (nonOutcome.length === 0) return;
    if (nonOutcome.some((a) => !a.role)) return;

    emit?.({
      X: nonOutcome
        .filter((a) => a.role === "X")
        .map((a) => ({ label: a.label })),
      M: nonOutcome
        .filter((a) => a.role === "M")
        .map((a) => ({ label: a.label })),
      Z: nonOutcome
        .filter((a) => a.role === "Z")
        .map((a) => ({ label: a.label })),
      Y: outcome.label,
    });
  };

  return (
    <div
      className="rounded-lg border border-slate-700 bg-slate-900 p-5 space-y-6"
      style={{ overflowAnchor: "none" }}
    >
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-amber-300">
          Confirm variables for Directed Acyclic Graph (DAG) construction
        </h3>

        <div className="text-sm font-bold text-slate-200 leading-relaxed">
          PAT-GPT has identified candidate variables from your input, but causal
          interpretation cannot proceed until you confirm exactly one focal
          outcome.
        </div>

        <div className="text-sm text-slate-300 leading-relaxed">
          Select the concepts you want to carry forward, nominate one outcome
          (Y), then assign provisional roles to the remaining selected concepts
          as candidate causes, exposures, conditions, or interventions (X),
          pathway variables (M), or background variables (Z).
        </div>
      </div>

      {localAtoms.length === 0 ? (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/20 p-3 text-xs text-amber-200 leading-relaxed">
          No candidate variables are available for confirmation. Add one focal
          outcome (Y), then add at least one relevant X, M, or Z variable
          manually.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 bg-slate-950/30 p-5 space-y-4">
          <div className="text-sm font-semibold text-slate-200">
            Candidate concepts and provisional roles
          </div>

          <div
            className="grid items-center gap-3 text-xs text-slate-400"
            style={{ gridTemplateColumns: ATOM_POOL_GRID_TEMPLATE }}
          >
            <div className="flex justify-center">Use</div>
            <div className="flex justify-center">Outcome</div>
            <div className="flex justify-center">Role</div>
            <div className="pl-1">Variable</div>
          </div>

          <div className="space-y-2">
            {localAtoms.map((a) => {
              const isSelected = a.selected;
              const isOutcome = outcomeId === a.id;
              const roleDisplayValue = isOutcome ? OUTCOME_ROLE_VALUE : a.role;

              return (
                <div
                  key={a.id}
                  className={`grid items-start gap-3 rounded-md border p-2 ${
                    isOutcome
                      ? "border-sky-500/70 bg-sky-950/20"
                      : isSelected
                        ? "border-slate-700 bg-slate-900/60"
                        : "border-slate-800 bg-slate-950/20"
                  }`}
                  style={{ gridTemplateColumns: ATOM_POOL_GRID_TEMPLATE }}
                >
                  <div className="flex justify-center pt-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(a.id)}
                      className="h-4 w-4 shrink-0"
                    />
                  </div>

                  <div className="flex justify-center pt-2">
                    <input
                      type="radio"
                      name="pat-outcome"
                      checked={isOutcome}
                      disabled={!isSelected}
                      onChange={() => chooseOutcome(a.id)}
                      className="h-4 w-4 shrink-0"
                    />
                  </div>

                  <select
                    value={roleDisplayValue}
                    disabled={!isSelected || isOutcome}
                    onChange={(e) => updateRole(a.id, e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 disabled:opacity-50"
                  >
                    <option value="">{isOutcome ? "Y" : "Select role"}</option>
                    <option value="X">X</option>
                    <option value="M">M</option>
                    <option value="Z">Z</option>
                  </select>

                  <div className="space-y-1">
                    <input
                      value={a.label}
                      onChange={(e) => updateLabel(a.id, e.target.value)}
                      className={`w-full bg-slate-800 border rounded px-2 py-1 text-sm text-slate-200 ${
                        isSuspiciousLabel(a.label)
                          ? "border-amber-500/70"
                          : "border-slate-700"
                      }`}
                    />

                    {isSuspiciousLabel(a.label) ? (
                      <div className="text-[11px] text-amber-300">
                        {suspiciousLabelReason(a.label)}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-xs text-slate-500 leading-relaxed">
            Use the checkbox to carry a concept forward. Select exactly one
            checked concept as the focal outcome (Y). All other selected
            concepts require a provisional X, M, or Z causal role before
            promotion.
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-700 bg-slate-950/30 p-5 space-y-4">
        <div className="text-sm font-semibold text-slate-200">
          Add concept manually
        </div>

        <div className="flex gap-2">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Add concept (e.g. biological predisposition)"
            className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200"
            onKeyDown={(e) => {
              if (e.key === "Enter") addAtom();
            }}
          />

          <button
            type="button"
            onClick={addAtom}
            className="px-4 py-1 rounded bg-slate-700 text-white hover:bg-slate-600"
          >
            Add
          </button>
        </div>
      </div>

      <div className="min-h-[2.5rem] space-y-1">
        {outcomeLooksSuspicious ? (
          <p className="text-xs text-amber-300">
            The nominated outcome appears clause-like or compound. Please edit
            it into a single clean concept before promotion.
          </p>
        ) : null}

        {missingRoleCount > 0 ? (
          <p className="text-xs text-amber-300">
            Assign a provisional role to each selected non-outcome variable
            before continuing.
          </p>
        ) : null}
      </div>

      <div className="border-t border-slate-700 pt-4 space-y-4">
        <label className="flex items-start gap-2 text-xs text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />

          <span>
            I confirm that these provisional variable selections and roles
            reasonably reflect the causal question I want PAT-GPT to model.
          </span>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={promote}
            className="px-4 py-2 text-xs rounded bg-indigo-600 text-white disabled:opacity-40"
            disabled={!canPromote}
          >
            Promote selected concepts to DAG
          </button>
        </div>
      </div>

      <p className="text-[15px] text-slate-500 italic">
        This panel stabilises variable meaning. Causal structure is confirmed
        later using the Directed Acyclic Graph (DAG).
      </p>
    </div>
  );
}
